import { z } from 'zod';

import { DETECTIONS, RoleKeySchema, SeveritySchema } from '../enums.js';

// Customer rules set at onboarding (spec §7, plan Appendix C). alert-service loads them from
// ORG_SETTINGS_FILE and serves them to other services at `GET /internal/settings`.

const perDetection = <T extends z.ZodType>(value: T) =>
  z.object(
    Object.fromEntries(DETECTIONS.map((d) => [d, value])) as Record<(typeof DETECTIONS)[number], T>,
  );

export const OrgSettingsSchema = z.object({
  severity: z.object({
    /** Base level per detection (spec §7: 0 Low, 1 Medium, 2 High). */
    base: perDetection(SeveritySchema),
    /** One level each, capped (spec §3). */
    uplifts: z.object({
      /** A PO releasing within this many days raises severity. */
      poWithinDays: z.number().int().nonnegative(),
      productionStatus: z.boolean(),
      highValueLine: z.boolean(),
    }),
    cap: SeveritySchema,
  }),
  /** Default owner role per detection, plus the diagnoses that override it (BR-025). */
  defaultOwnerRole: perDetection(RoleKeySchema).extend({
    erp_ahead_of_pdm: RoleKeySchema,
    /** BR-006: who gets an alert when nobody holds its role. */
    fallback: RoleKeySchema,
  }),
  timers: z.object({
    timeoutBusinessDays: z.number().int().positive(),
    verificationGraceBusinessDays: z.number().int().nonnegative(),
  }),
  external: z.object({
    eligibleSeverity: SeveritySchema,
    immediatePoWithinDays: z.number().int().nonnegative(),
    batchWindowMinutes: z.number().int().positive(),
    channel: z.enum(['org_email', 'push']),
  }),
  normalization: z.object({
    ignoreCase: z.boolean(),
    ignoreWhitespace: z.boolean(),
    ignoreLeadingZeros: z.boolean(),
  }),
  /** Configured email recipients by key, e.g. `supply_team` (spec §9: no hard-coded addresses). */
  recipients: z.record(z.string(), z.email()),
  watchCycle: z.enum(['daily']),
  managers: z.array(z.email()).min(1),
});
export type OrgSettings = z.infer<typeof OrgSettingsSchema>;

/** `GET /internal/settings`: the rules other services apply (e.g. compare's normalization). */
export const SettingsResponseSchema = z.object({ settings: OrgSettingsSchema });
export type SettingsResponse = z.infer<typeof SettingsResponseSchema>;

/** `GET /internal/visibility/:userId/assemblies` (BR-021): the assemblies a user can see. */
export const VisibilityResponseSchema = z.object({ assemblyIds: z.array(z.uuid()) });
export type VisibilityResponse = z.infer<typeof VisibilityResponseSchema>;
